<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading"
        ></v-progress-circular>
        <v-card v-else max-width="650px" width="650px">
          <v-card-title class="ml-4">
            <span class="headline" v-if="editView">{{
              $t("tasks.tasksAddEdit.editTask")
            }}</span>
            <span class="headline" v-else>{{
              $t("tasks.tasksAddEdit.newTask")
            }}</span>
            <v-btn
              v-if="canUseKnowledgeBaseBtn"
              @click="handleHelp"
              icon
              color="primary"
            >
              <v-icon class="mx-2">mdi-help-circle</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn
                text
                v-if="editView && checkFinishedTask"
                color="primary"
                @click="reopenTask($route.params.id)"
                ><v-icon small class="mx-2">mdi-lock-open</v-icon
                >{{ $t("tasks.tasksAddEdit.reopenTask") }}</v-btn
              >
              <v-btn
                text
                v-if="editView"
                color="primary"
                @click="previewTask($route.params.id)"
                ><v-icon small class="mx-2">mdi-calendar-check</v-icon
                >{{ $t("tasks.tasksAddEdit.preview") }}</v-btn
              >
              <v-btn
                text
                color="red"
                class="mr-1"
                v-if="editView"
                @click="activateDeleteDialog()"
              >
                <v-icon small class="mx-2">mdi-delete</v-icon
                >{{ $t("tasks.tasksAddEdit.delete") }}</v-btn
              >
            </v-card-actions>
          </v-card-title>

          <v-form
            ref="formTitle"
            @submit.prevent="saveTask"
            v-model="validTitle"
            lazy-validation
          >
            <v-card-text class="pb-0">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        <v-text-field
                          :label="$t('tasks.tasksAddEdit.title')"
                          persistent-hint
                          color="primary"
                          v-model="editedItem.name"
                          :hint="$t('tasks.tasksAddEdit.required')"
                          :rules="titleRule"
                          autocomplete="off"
                          :disabled="checkFinishedTask"
                        />
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        <v-select
                          :label="$t('tasks.tasksAddEdit.status')"
                          v-model="editedItem.status"
                          :items="statusOptions"
                          item-text="name"
                          item-value="status"
                          color="primary"
                          :disabled="checkFinishedTask"
                        >
                        </v-select>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        <date-picker
                          :date-label="$t('tasks.tasksAddEdit.dueDate')"
                          :provider-date.sync="editedItem.due_date"
                          :preventPastDate="true"
                          :disabled="checkFinishedTask"
                          :hint="suggestedDueDate"
                        />
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle>
                        <v-select
                          v-model="editedItem.duration"
                          :label="$t('tasks.tasksAddEdit.duration')"
                          color="primary"
                          :items="durations"
                          item-text="duration"
                          item-value="value"
                          :disabled="checkFinishedTask"
                        ></v-select>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-subtitle style="font-size: 0.75rem">{{
                        $t("tasks.tasksAddEdit.priority")
                      }}</v-list-item-subtitle>
                      <v-rating
                        v-model="editedItem.priority"
                        length="4"
                        :readonly="checkFinishedTask"
                      >
                        <template v-slot:item="props">
                          <v-icon
                            @click="props.click"
                            :color="props.isFilled ? 'primary' : ''"
                            v-text="`mdi-numeric-${props.index + 1}-box`"
                          ></v-icon>
                        </template>
                      </v-rating>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="canSeeEstimatedCost" class="mb-2">
                <v-col cols="6">
                  <v-list-item>
                    <v-text-field
                      :label="$t('tasks.tasksAddEdit.estimatedCost')"
                      persistent-hint
                      color="primary"
                      v-model="editedItem.estimated_cost"
                      :disabled="checkFinishedTask"
                      autocomplete="off"
                      type="number"
                    />
                  </v-list-item>
                </v-col>
                <v-col cols="6" v-if="creatorOptions.length > 1">
                  <v-list-item>
                    <v-select
                      :label="$t('tasks.tasksAddEdit.scope')"
                      :hint="$t('tasks.tasksAddEdit.required')"
                      v-model="editedItem.crm_partner_uuid"
                      :items="creatorOptions"
                      item-text="name"
                      item-value="uuid"
                      :disabled="checkFinishedTask"
                      :rules="creatorRule"
                    >
                    </v-select>
                  </v-list-item>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="grey--text text--darken-1"
                        >{{ $t("tasks.tasksAddEdit.checklists") }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col cols="6">
                  <v-btn
                    v-if="!checkFinishedTask"
                    class="float-right mt-2"
                    text
                    @click="openChecklistSelector"
                    color="primary"
                    ><v-icon class="mr-1">mdi-plus-circle</v-icon
                    >{{ $t("tasks.tasksAddEdit.addChecklist") }}</v-btn
                  >
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col cols="12">
                  <div v-if="sameServicingPartner.active">
                    <v-row no-gutters>
                      <v-col cols="12" class="ml-4 pr-8">
                        <task-dealer-selector
                          :initial-dealer.sync="
                            sameServicingPartner.crm_partner
                          "
                          :select-first="true"
                          :label="$t('tasks.tasksAddEdit.servicingPartner')"
                          @change="handleDealerSelected"
                          :disabled="checkFinishedTask"
                        />
                      </v-col>
                    </v-row>
                    <v-row
                      no-gutters
                      v-if="
                        sameServicingPartner.crm_partner_uuid &&
                        sameServicingPartner.crm_partner_uuid !== 'other'
                      "
                    >
                      <v-col cols="12" class="ml-4 pr-8">
                        <task-assignee-selector
                          :initial-data="sameServicingPartner.assignee"
                          :professional="sameServicingPartner.crm_partner_uuid"
                          @change="handleAssigneeSelected"
                          :disabled="checkFinishedTask"
                        ></task-assignee-selector>
                      </v-col>
                    </v-row>
                    <v-row
                      no-gutters
                      v-if="
                        sameServicingPartner.crm_partner_uuid &&
                        sameServicingPartner.crm_partner_uuid === 'other'
                      "
                    >
                      <v-col cols="12" class="ml-4 pr-8 mt-4">
                        <v-text-field
                          v-model="
                            sameServicingPartner.unrecognizedAssigneeEmail
                          "
                          class="mt-0 pt-0"
                          color="primary"
                          :label="$t('tasks.tasksAddEdit.enterEMailAddress')"
                          autocomplete="off"
                          :disabled="checkFinishedTask"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <template v-for="(checklist, index) in editedItem.checklists">
                    <checklist-add-edit
                      :key="checklist.vueId"
                      :professional="editedItem.crm_partner_uuid"
                      :checklist-index="index"
                      :checklist="checklist"
                      :content-saved.sync="contentSaved"
                      :disabled="checkFinishedTask"
                      :boat-disabled="isBoatDisabled"
                      :same-servicing-partner="sameServicingPartner"
                      @delete:checklist="deleteChecklist"
                      @update:checklist="updateChecklist"
                      @update:sameServicingPartner="updateSameServicingPartner"
                      ref="checklistAddEdit"
                    ></checklist-add-edit>
                  </template>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12">
                  <v-list-item>
                    <v-list-item-content>
                      <!-- <v-list-item-title>Comment:</v-list-item-title> -->
                      <v-list-item-subtitle>
                        <v-textarea
                          label="Description"
                          color="primary"
                          v-model="editedItem.description"
                          rows="3"
                          auto-grow
                          :disabled="checkFinishedTask"
                        >
                        </v-textarea
                      ></v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                v-if="editView && editedItem.attachments.length"
              >
                <v-col cols="12">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title
                        v-if="editedItem.attachments.length"
                        class="mb-1"
                        >{{
                          $t("tasks.tasksAddEdit.attachments")
                        }}</v-list-item-title
                      >
                      <v-list-item-action-text>
                        <template
                          v-for="(attachment, index) in editedItem.attachments"
                        >
                          <v-row :key="index">
                            <v-col cols="10">
                              <v-btn @click="openAttachment(attachment)" text
                                ><v-icon color="grey darken-2"
                                  >mdi-file-image</v-icon
                                >
                                <v-list-item-subtitle
                                  class="text-none subtitle-2 ml-2"
                                  >{{ attachment.title }}</v-list-item-subtitle
                                ></v-btn
                              >
                            </v-col>
                            <v-col cols="2" v-if="editView">
                              <v-btn
                                :disabled="!checkFinishedTask"
                                text
                                @click="removeTaskAttachment(index)"
                                ><v-icon color="red">mdi-delete</v-icon></v-btn
                              >
                            </v-col>
                          </v-row>
                        </template>
                      </v-list-item-action-text>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-row>
            </v-card-text>
          </v-form>
          <v-card-actions>
            <template>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="close()">{{
                $t("tasks.tasksAddEdit.cancel")
              }}</v-btn>
              <v-btn color="primary" text class="mr-2" @click="saveTask">{{
                $t("tasks.tasksAddEdit.save")
              }}</v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-layout>
    </v-container>
    <delete-dialog
      :dialog="deleteDialog"
      :item="taskName"
      @agree="remove"
      v-show="false"
    />
    <help-dialog
      :dialog="helpDialog"
      @close="closeHelpDialog"
      v-if="helpDialog.active"
    ></help-dialog>
    <checklist-template-selector
      :dialog="checklistSelectorDialog"
      v-if="checklistSelectorDialog.active"
      @create="handleCreateChecklist"
      @close="handleCloseChecklist"
      @add="handleAddChecklist"
    >
    </checklist-template-selector>
  </v-content>
</template>

<script>
import api from "@/utils/api.js";
import snackbar from "@/utils/snackbar";
import DatePicker from "@/components/pickers/DatePicker";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import { mapGetters, mapState } from "vuex";
import ChecklistAddEdit from "@/components/checklists/ChecklistAddEdit.vue";
import HelpDialog from "@/components/dialogs/HelpDialog";
import constraints from "@/utils/constraints";
import ChecklistTemplateSelector from "@/components/checklists/ChecklistTemplateSelector.vue";
import TaskDealerSelector from "@/components/selectors/TaskDealerSelector";
import TaskAssigneeSelector from "@/components/selectors/TaskAssigneeSelector";
import restrictions from "@/utils/restrictions";

export default {
  components: {
    ChecklistAddEdit,
    DatePicker,
    DeleteDialog,
    HelpDialog,
    ChecklistTemplateSelector,
    TaskDealerSelector,
    TaskAssigneeSelector,
  },
  data: () => ({
    canUseKnowledgeBaseBtn: false,
    helpDialog: { location: null, active: false },
    servicingPartnerRule: [],
    titleRule: [],
    creatorRule: [],
    validDealer: true,
    validTitle: true,
    deleteDialog: false,
    taskName: "",
    statusOptions: [],
    durations: [
      { duration: "15m", value: 15 },
      { duration: "30m", value: 30 },
      { duration: "45m", value: 45 },
      { duration: "1h", value: 60 },
      { duration: "2h", value: 120 },
      { duration: "4h", value: 240 },
      { duration: "8h", value: 480 },
      { duration: "12h", value: 720 },
      { duration: "16h", value: 960 },
      { duration: "24h", value: 1440 },
    ],
    sameServicingPartner: {
      active: true,
      assignee_email: "",
      crm_partner: {},
      assignee: {},
      crm_partner_uuid: null,
      updateAssigneeList: 0,
      unrecognizedAssigneeEmail: "",
    },
    editView: false,
    isBoatDisabled: false,
    suggestedDueDate: "",
    loading: true,
    contentSaved: true,
    professionalSelected: false,
    unrecognizedAssigneeEmail: "",
    checklistSelectorDialog: { active: false, response: null },
    editedItem: {
      assignee: {},
      estimated_cost: 0,
      crm_partner_uuid: null,
      checklists: [{ assignee: null }],
      reports: [
        { engine_seconds_requested: "", crm_service: { short_task: "" } },
      ],
      subtasks: [],
      status: "",
    },
  }),

  computed: {
    ...mapGetters(["getIframeStatus", "getMe"]),
    getStatus() {
      return this.statusOptions.find(
        (item) => item.status === this.editedItem.status
      ).name;
    },
    checkFinishedTask() {
      return !!this.editedItem.completed;
    },
    getDuration() {
      let duration = this.durations.find(
        (item) => item.value === this.editedItem.duration
      );
      return typeof duration === "undefined"
        ? this.editedItem.duration
        : duration.duration;
    },

    modelPresent() {
      return !!this.editedItem.crm_boat;
    },
    canSeeEstimatedCost: () => {
      return !!restrictions.canUseCrmFeature("can_see_estimated_cost");
    },
    creatorOptions() {
      return this.getMe.dealerships;
    },
  },

  created() {
    this.setInitalData();
    this.canUseKnowledgeBaseBtn = constraints.canUseKnowledgeBaseBtn("tasks");
    this.getView();
    if (this.editView) {
      this.loadTask();
    } else {
      this.editedItem.name = this.$route.query.title;
      this.editedItem.description = this.$route.query.comment;
      this.editedItem.status = this.statusOptions[0].status;
      this.editedItem.checklists = [this.setNewBoatTemplate()];
      this.loading = false;
    }
  },

  mounted() {
    this.$store.subscribe((mutation, state) => {
      if (
        mutation.type === "SET_SERVICES" &&
        typeof state.task.services !== "undefined"
      ) {
        this.handleExternalTask(state.task);
      }
    });
  },

  watch: {
    $route(newValue, oldValue) {
      this.getView();
    },
  },

  methods: {
    setInitalData() {
      this.servicingPartnerRule = [
        (v) => !!v || this.$t("tasks.tasksAddEdit.servicingPartnerIsRequired"),
      ];
      this.titleRule = [
        (v) => !!v || this.$t("tasks.tasksAddEdit.titleIsRequired"),
      ];

      this.creatorRule = [
        (v) => !!v || this.$t("tasks.tasksAddEdit.scopeIsRequired"),
      ];

      this.statusOptions = [
        {
          status: "NOT_STARTED",
          name: this.$t("tasks.tasksAddEdit.notStarted"),
        },
        {
          status: "IN_PROGRESS",
          name: this.$t("tasks.tasksAddEdit.inProgress"),
        },
        { status: "COMPLETED", name: this.$t("tasks.tasksAddEdit.completed") },
      ];

      //TODO translations
      this.durations = [
        { duration: "15m", value: 15 },
        { duration: "30m", value: 30 },
        { duration: "45m", value: 45 },
        { duration: "1h", value: 60 },
        { duration: "2h", value: 120 },
        { duration: "4h", value: 240 },
        { duration: "8h", value: 480 },
        { duration: "12h", value: 720 },
        { duration: "16h", value: 960 },
        { duration: "24h", value: 1440 },
      ];

      this.editedItem.creator =
        this.getMe.dealerships.length === 1
          ? this.getMe.dealerships[0].uuid
          : null;
    },
    updateSameServicingPartner(value) {
      this.sameServicingPartner = { ...this.sameServicingPartner, ...value };
      const { updateAssigneeList, active, ...rest } = this.sameServicingPartner;
      this.editedItem.checklists = this.editedItem.checklists.map(
        (checklist) => {
          return { ...checklist, ...rest };
        }
      );
    },
    handleAssigneeSelected(value) {
      this.sameServicingPartner.assignee = value;
      this.sameServicingPartner.assignee_email = value.username;
      this.sameServicingPartner.unrecognizedAssigneeEmail = "";
    },

    handleDealerSelected(dealer) {
      if (dealer === null) {
        this.sameServicingPartner = {
          active: true,
          assignee_email: "",
          crm_partner: {},
          assignee: {},
          crm_partner_uuid: null,
          updateAssigneeList: 0,
          unrecognizedAssigneeEmail: "",
        };
        return;
      }

      if (dealer.uuid === "other") {
        this.sameServicingPartner.assignee_email = null;
        this.sameServicingPartner.assignee = {};
      }
      this.sameServicingPartner.crm_partner = dealer;
      this.sameServicingPartner.crm_partner_uuid = dealer.uuid;
      this.sameServicingPartner.updateAssigneeList++;
    },
    handleCreateChecklist() {
      this.createChecklist();
      this.handleCloseChecklist();
    },
    handleAddChecklist(items) {
      this.handleExternalTask({ services: items, from: "template" }, true);
      this.handleCloseChecklist();
    },
    handleCloseChecklist() {
      this.checklistSelectorDialog.active = false;
    },
    openChecklistSelector() {
      api.getTemplateList().then((resp) => {
        if (resp.data.results.length !== 0) {
          this.checklistSelectorDialog.response = resp;
          this.checklistSelectorDialog.active = true;
        } else {
          this.createChecklist();
        }
      });
    },
    handleExternalTask(task, append = false) {
      let transformSubtasks = (subtasks, reportUuid = "") => {
        return subtasks.map((subtask) => {
          return {
            description: subtask.description,
            crm_service_subtask_uuid: subtask.uuid,
            crm_service_report_uuid: reportUuid,
          };
        });
      };

      if (!append) this.editedItem.checklists = [];

      if (task.from === "services") {
        this.editedItem.crm_partner_uuid = null;

        // this.editedItem.crm_partner_uuid = task.services[0].crm_partner.uuid;
        // this.professionalChanged(this.editedItem.crm_partner_uuid);

        this.isBoatDisabled = true;

        if (task.services.length == 1) {
          this.suggestedDueDate = this.$moment(
            task.services[0].should_be_done_before
          ).format("YYYY-MM-DD");
        }

        task.services.forEach((service) => {
          if (
            Object.keys(service.crm_service).length !== 0 &&
            Object.keys(service.crm_boat).length !== 0
          ) {
            this.editedItem.checklists.unshift(
              this.setSubtaskTemplate(
                service,
                transformSubtasks(service.crm_service.subtasks, service.uuid)
              )
            );
          }
        });
      } else if (task.from === "template") {
        task.services.forEach((service) => {
          let template = { subtasks: [], name: "" };
          service.checklists.forEach((element) => {
            template.name = element.name;
            template.subtasks = element.subtasks.map((subtask) => {
              return { description: subtask.description };
            });
            this.editedItem.checklists.unshift(
              this.setTemplate(template.name, template.subtasks)
            );
          });
        });
      }
    },
    reopenTask() {
      this.loading = true;
      api
        .reopenTask(this.editedItem.uuid, {
          status: "IN_PROGRESS",
          completed: null,
        })
        .then((resp) => {
          this.loading = false;
          this.editedItem.completed = false;
          this.editedItem.status = "IN_PROGRESS";
        })
        .catch((err) => {
          this.handleError(err.response);
          this.loading = false;
        });
    },
    closeHelpDialog(event) {
      if (event === "close") {
        this.helpDialog.active = false;
      }
    },
    handleHelp(event) {
      this.helpDialog.active = true;
      this.helpDialog.location = "tasks";
    },
    openAttachment(attachment) {
      !this.getIframeStatus
        ? window.open(attachment.file_link, "_blank")
        : window.open(attachment.file_link, "_self");
    },
    deleteChecklist(object) {
      const { checklist, index } = object;
      this.editedItem.checklists.splice(index, 1);
    },
    updateChecklist(object) {
      const { checklist, index } = object;
      this.$set(this.editedItem.checklists, index, checklist);
    },
    createChecklist() {
      if (this.editedItem.checklists.length === 0) {
        this.editedItem.checklists.unshift(this.setNewBoatTemplate());
      } else {
        this.editedItem.checklists.unshift(
          this.setNewBoatTemplateFromExistent(this.editedItem.checklists[0])
        );
      }
    },
    setNewBoatTemplateFromExistent(checklist) {
      const { uuid, crm_boat, ...filteredChecklist } = checklist;
      return {
        ...filteredChecklist,
        crm_boat: crm_boat,
        crm_boat_uuid:
          crm_boat !== null ? crm_boat.uuid : filteredChecklist.crm_boat_uuid,
        subtasks: [],
        all_subtasks: 0,
        vueId: Math.random() * 100000,
      };
    },
    setNewBoatTemplate(boat = null, subtasks = []) {
      return {
        vueId: Math.random() * 100000,
        crm_boat: boat,
        subtasks: subtasks,
        crm_boat_uuid: null,
        // name: `Checklist ${this.editedItem.checklists.length}`,
        name: null,
        crm_partner_uuid: null,
        crm_partner: {},
        assignee_email: null,
        assignee: {},
        unrecognizedAssigneeEmail: "",
      };
    },

    setSubtaskTemplate(service, subtasks = []) {
      return {
        vueId: Math.random() * 100000,
        crm_boat: service.crm_boat,
        subtasks: subtasks,
        crm_boat_uuid: service.crm_boat.uuid,
        name: service.crm_service.short_task,
        crm_partner_uuid: null,
        crm_partner: {},
        crm_boat_item: service.crm_boat_item,
        assignee_email: null,
        assignee: {},
        unrecognizedAssigneeEmail: "",
      };
    },

    setTemplate(name, subtasks) {
      return {
        vueId: Math.random() * 100000,
        crm_boat: {},
        subtasks: subtasks,
        crm_boat_uuid: null,
        name: name,
        crm_partner_uuid: null,
        crm_partner: {},
        assignee_email: null,
        assignee: {},
        unrecognizedAssigneeEmail: "",
      };
    },

    professionalChanged(event) {
      this.professionalSelected = typeof event !== "undefined";
    },

    getView() {
      this.editView = this.$route.name === "tasksEdit";
    },

    loadTask() {
      api.getTask(this.$route.params.id).then((resp) => {
        this.editedItem = Object.assign({}, resp.data);
        this.editedItem.priority = Number(this.editedItem.priority);

        if (
          this.editedItem.crm_partner !== null &&
          typeof this.editedItem.crm_partner !== "undefined"
        ) {
          this.editedItem.crm_partner_uuid = this.editedItem.crm_partner.uuid;
        }

        this.editedItem.estimated_cost = isNaN(
          parseFloat(this.editedItem.estimated_cost)
        )
          ? null
          : parseFloat(this.editedItem.estimated_cost);

        this.loading = false;

        let deepEqual = (x, y) => {
          const ok = Object.keys,
            tx = typeof x,
            ty = typeof y;
          return x && y && tx === "object" && tx === ty
            ? ok(x).length === ok(y).length &&
                ok(x).every((key) => deepEqual(x[key], y[key]))
            : x === y;
        };

        //Check for duplicate crm_partner uuid
        if (this.editedItem.checklists.length === 0) {
          return;
        }

        if (this.editedItem.checklists.length === 1) {
          this.$set(this.sameServicingPartner, "active", true);
          this.sameServicingPartner = {
            ...this.sameServicingPartner,
            active: true,
            assignee: this.editedItem.checklists[0].assignee,
            crm_partner:
              this.editedItem.checklists[0].crm_partner === null &&
              this.editedItem.checklists[0].assignee.is_external
                ? { name: this.$t("tasks.tasksAddEdit.other"), uuid: "other" }
                : this.editedItem.checklists[0].crm_partner,
            crm_partner_uuid: this.editedItem.checklists[0].crm_partner_uuid,
            unrecognizedAssigneeEmail:
              this.editedItem.checklists[0].crm_partner === null &&
              this.editedItem.checklists[0].assignee.is_external
                ? this.editedItem.checklists[0].assignee.email
                : "",
          };
          return;
        }

        let array = [];
        this.editedItem.checklists
          .map((checklist) => {
            return {
              crm_partner: checklist.crm_partner,
              assignee: { ...checklist.assignee, uuid: 0 }, //api returns different uuids on same assignees
            };
          })
          .reduce((prev, next, ind, arr) => {
            array.push(deepEqual(prev, next));
            return next;
          });

        if (array.every((element) => element)) {
          this.sameServicingPartner = {
            ...this.sameServicingPartner,
            active: true,
            assignee: this.editedItem.checklists[0].assignee,
            crm_partner:
              this.editedItem.checklists[0].crm_partner === null &&
              this.editedItem.checklists[0].assignee.is_external
                ? { name: this.$t("tasks.tasksAddEdit.other"), uuid: "other" }
                : this.editedItem.checklists[0].crm_partner,
            crm_partner_uuid: this.editedItem.checklists[0].crm_partner_uuid,
            unrecognizedAssigneeEmail:
              this.editedItem.checklists[0].crm_partner === null &&
              this.editedItem.checklists[0].assignee.is_external
                ? this.editedItem.checklists[0].assignee.email
                : "",
          };
        } else {
          this.$set(this.sameServicingPartner, "active", false);
          setTimeout(() => {
            this.$refs.checklistAddEdit.forEach((element) => {
              element.handleHideChangeServicingPartner();
            });
          }, 0);
        }
      });
    },

    close() {
      this.$router.push({
        name: "tasks",
      });
    },

    previewTask(taskUuid) {
      this.$router
        .pushAsync({
          name: "tasksDetails",
          params: { id: taskUuid },
        })
        .then((resp) => {
          this.loading = false;
        });
    },
    emptyChecklist(checklists) {
      if (typeof checklists === "undefined" || checklists.length === 0) {
        return true;
      }

      return checklists.some((item) => {
        return item.subtasks.length === 0;
      });
    },

    saveTask() {
      if (this.emptyChecklist(this.editedItem.checklists)) {
        snackbar.error(
          this.$t("utils.snackbar.error.taskChecklistShouldNotBeEmpty")
        );
        return;
      }

      if (this.$refs.formTitle.validate()) {
        if (this.editedItem.status === "COMPLETED") {
          this.editedItem.completed = new Date();
        } else {
          this.editedItem.completed = null;
        }

        this.editedItem.crm_group_uuid = this.getMe.crm_groups[0].uuid;

        if (typeof this.editedItem.attachments !== "undefined") {
          this.editedItem.attachment_uuids = this.editedItem.attachments.map(
            (item) => item.uuid
          );
        }

        this.editedItem.checklists = this.editedItem.checklists.filter(
          (checklist) => {
            return checklist.subtasks.length !== 0;
          }
        );

        this.editedItem.checklists.forEach((checklist) => {
          if (checklist.crm_partner_uuid === "other") {
            checklist.crm_partner_uuid = null;
            checklist.assignee_email = checklist.unrecognizedAssigneeEmail;
            delete checklist.unrecognizedAssigneeEmail;
          }
        });
        if (this.sameServicingPartner.active) {
          this.editedItem.checklists = this.editedItem.checklists.map(
            (checklist) => {
              return {
                ...checklist,
                crm_partner_uuid:
                  this.sameServicingPartner.crm_partner_uuid === "other"
                    ? null
                    : this.sameServicingPartner.crm_partner.uuid,
                // assignee:
                //   this.sameServicingPartner.unrecognizedAssignee === ""
                //     ? null
                //     : this.sameServicingPartner.assignee,
                assignee_email:
                  this.sameServicingPartner.unrecognizedAssigneeEmail !== ""
                    ? this.sameServicingPartner.unrecognizedAssigneeEmail
                    : this.sameServicingPartner.assignee_email,
              };
            }
          );
        }

        if (this.editView) {
          this.loading = true;
          //false parameter is here for disabling error handling
          api
            .updateTask(this.editedItem.uuid, this.editedItem, false)
            .then((resp) => {
              snackbar.success(
                this.$t("utils.snackbar.success.successfullySaved")
              );
              this.previewTask(resp.data.uuid);
            })
            .catch((err) => {
              this.handleError(err.response);
              this.loading = false;
            });
        } else {
          delete this.editedItem.crm_partner_uuid;
          //false parameter is here for disabling error handling
          api
            .createTask(this.editedItem, false)
            .then((resp) => {
              snackbar.success(
                this.$t("utils.snackbar.success.successfullySaved")
              );
              this.previewTask(resp.data.uuid);
            })
            .catch((err) => {
              this.handleError(err.response);
              this.loading = false;
            });
        }
      } else {
        this.handleError();
      }
    },

    removeSubtask(ind) {
      this.editedItem.subtasks.splice(ind, 1);
    },

    saveSubtask(event) {
      let data = this.editedItem.subtasks;

      data.forEach((element) => {
        if (element.completed === true) {
          element.completed = new Date().toISOString();
        }
        if (element.completed === false) {
          element.completed = null;
        }
        if (element.comment === " ") {
          element.comment = "";
        }
      });

      this.contentSaved = false;
      api
        .saveSubtask(this.$route.params.id, data)
        .then((resp) => {
          this.contentSaved = true;
        })
        .catch((err) => {
          this.contentSaved = false;
        });
    },

    removeTaskAttachment(index) {
      this.editedItem.attachments.splice(index, 1);
    },

    handleError(err) {
      if (err) {
        snackbar.error(err.response);
      } else {
        snackbar.error();
      }
    },

    activateDeleteDialog() {
      this.deleteDialog = true;
      this.taskName = "task " + this.editedItem.name;
    },

    remove(agreed) {
      if (agreed) {
        api
          .deleteTask(this.editedItem.uuid)
          .then((resp) => {
            snackbar.success(
              this.$t("utils.snackbar.success.successfullyDeleted")
            );
            this.close();
          })
          .catch((err) => {
            snackbar.error();
          });
      }
      this.deleteDialog = false;
    },
  },
};
</script>
<style scoped></style>
